/**
 * Keeps exclusion-heavy Box folder rows compact while preserving access to every configured entry.
 */
import React, { useRef, useState } from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Popover from '@amzn/meridian/popover';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';

interface Props {
  readonly entries?: readonly string[];
}

export const BoxFolderExclusionsCell: React.FC<Props> = ({ entries }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const countButtonRef = useRef();

  if (!entries?.length) {
    return <Text>{t('box_folder_exclusions_empty')}</Text>;
  }

  return (
    <>
      <Button
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={t('box_folder_exclusions_count', { count: entries.length })}
        data-testid="box-folder-exclusions-count"
        propagateClickEvent={false}
        ref={countButtonRef}
        type="link"
        onClick={() => {
          setIsOpen((open) => !open);
        }}
      >
        {entries.length}
      </Button>
      <Popover
        anchorNode={countButtonRef.current}
        maxWidth="min(480px, 80vw)"
        open={isOpen}
        position="bottom"
        onClose={() => {
          setIsOpen(false);
        }}
      >
        <Column
          data-testid="box-folder-exclusions-list"
          maxHeight="60vh"
          maxWidth="100%"
          overflowY="auto"
          role="list"
          spacing="100"
        >
          {entries.map((entry, index) => (
            <Text
              breakWord={true}
              data-testid="box-folder-exclusion-entry"
              key={`${index}-${entry}`}
              role="listitem"
              tag="div"
            >
              {entry}
            </Text>
          ))}
        </Column>
      </Popover>
    </>
  );
};
